<template>
  <div class="d-flex flex-column" style="height: 100vh; width: 100vw">
    <header v-if="mailboxes.length > 1" class="scheduler-toolbar d-flex align-center px-4">
      <v-card color="primary" flat class="pa-1 mr-3 d-flex">
        <v-img height="22" width="22" :src="brandIconWhite" />
      </v-card>
      <span class="text-subtitle-1 font-weight-medium">Scheduling Pages</span>
      <v-spacer />
      <v-select
        v-model="selectedMailboxId"
        :items="mailboxItems"
        item-text="email"
        item-value="id"
        dense
        outlined
        hide-details
        class="scheduler-toolbar__select"
        prepend-inner-icon="mdi-email-outline"
        :menu-props="{ offsetY: true, nudgeBottom: 4 }"
      >
        <template v-slot:selection="{ item }">
          <span class="text-body-2 text-truncate">{{ item.email }}</span>
          <v-chip v-if="item.is_primary" x-small color="primary" class="ml-2 flex-shrink-0">
            Primary
          </v-chip>
        </template>
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title class="text-body-2">
              {{ item.email }}
            </v-list-item-title>
            <v-list-item-subtitle class="text-caption">
              {{ item.email_provider }}
            </v-list-item-subtitle>
          </v-list-item-content>
          <v-chip v-if="item.is_primary" x-small color="primary" class="ml-2 flex-shrink-0">
            Primary
          </v-chip>
        </template>
      </v-select>
    </header>
    <nylas-scheduler-editor
      :key="editorKey"
      style="flex: 1 1 auto; width: 100vw; min-height: 0"
    />
  </div>
</template>

<script>
import { CustomIdentityRequestWrapperProxy } from '@/utils/nylasApiRequestWrapper.js'
import { mapGetters, mapMutations } from 'vuex'
import brandIconWhite from '@/assets/img/brand/Symbo_Icon_White.svg'

export default {
  name: 'SchedulerView',
  data: () => ({
    brandIconWhite,
  }),
  computed: {
    ...mapGetters('user', ['mailbox', 'mailboxes']),
    mailboxItems() {
      return this.mailboxes.map((m) => ({
        id: m.id,
        email: m.email,
        email_provider: m.email_provider,
        is_primary: m.is_primary,
      }))
    },
    selectedMailboxId: {
      get() {
        return this.mailbox?.id
      },
      set(mailboxId) {
        this.setSelectedMailboxId(mailboxId)
      },
    },
    // Recreate the editor web component whenever the mailbox changes — it
    // holds grant-specific state that cannot be swapped in place
    editorKey() {
      return this.mailbox?.id || 'no-mailbox'
    },
  },
  watch: {
    editorKey() {
      this.$nextTick(() => this.initEditor())
    },
  },
  methods: {
    ...mapMutations('user', ['setSelectedMailboxId']),
    initEditor() {
      const schedulerEditor = document.querySelector('nylas-scheduler-editor')
      if (!schedulerEditor || !this.mailbox) return

      // TEMP debug (SYM-520): trace which mailbox the editor is initialized
      // with, to verify the clicked mailbox is the one actually used
      console.log('[SchedulerView] initEditor payload', {
        routeMailboxId: this.$route.query.mailboxId,
        storeSelectedMailboxId: this.$store.state.user.selectedMailboxId,
        mailbox: {
          id: this.mailbox.id,
          email: this.mailbox.email,
          is_primary: this.mailbox.is_primary,
          grant_id: this.mailbox.grant_id,
          email_provider: this.mailbox.email_provider,
          app_client_id: this.mailbox.app_client_id,
        },
        allMailboxes: this.mailboxes.map((m) => ({
          id: m.id,
          email: m.email,
          is_primary: m.is_primary,
        })),
      })

      const grantId = this.mailbox.grant_id
      const email = this.mailbox.email
      const domain = process.env.VUE_APP_SYMBO_API_URL
      const provider = this.mailbox.email_provider
      const symboAuthToken = localStorage.getItem('token')
      const mailboxId = this.mailbox.id
      const nylasApiRequest = new CustomIdentityRequestWrapperProxy({
        grantId,
        email,
        domain,
        symboAuthToken,
        provider,
        mailboxId,
        isStytchToken: true,
      })
      schedulerEditor.nylasApiRequest = nylasApiRequest
      const baseUrl = process.env.VUE_APP_SYMBO_UI_URL
      // Configurations live in the Nylas app the mailbox was connected with,
      // so the preview link must use that app's client id — the env id only
      // matches mailboxes on the default app and is kept as a fallback.
      const appClientId = this.mailbox.app_client_id || process.env.VUE_APP_NYLAS_APP_ID
      schedulerEditor.schedulerPreviewLink = `${baseUrl}/book/${appClientId}/{slug}`

      schedulerEditor.requiresSlug = true
      schedulerEditor.conferenceProviders = { google: grantId }
      schedulerEditor.eventOverrides = {
        formSubmitted: (e) => {
          console.log('formSubmitted', e)
        },
        createButtonClick: (e) => {
          console.log('createButtonClick', e)
        },
      }

      // Style overrides
      var styleOverrides = document.createElement('style')
      styleOverrides.innerHTML = `
            .sc-nylas-editor-tabs-h { height: 100% !important; }
            .tab-content.sc-nylas-editor-tabs { max-width: unset !important; }
            .list-configurations { height: calc(100% - 24px) !important; }
            .scheduler-editor-close { display: none !important; }
          `
      schedulerEditor?.shadowRoot?.appendChild(styleOverrides)
    },
  },
  async mounted() {
    // Dynamically import nylas web component only on client
    if (typeof window !== 'undefined') {
      await import('@nylas/web-elements/dist/nylas-web-elements/nylas-web-elements.esm.js')
    }

    // Deep link: Symbo Settings can open the editor for a specific mailbox
    const requestedMailboxId = this.$route.query.mailboxId
    if (requestedMailboxId && this.mailboxes.some((m) => m.id === requestedMailboxId)) {
      this.setSelectedMailboxId(requestedMailboxId)
    }

    this.$nextTick(() => this.initEditor())
  },
}
</script>

<style scoped>
.scheduler-toolbar {
  flex: 0 0 auto;
  height: 60px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  background: #fff;
}
.scheduler-toolbar__select {
  max-width: 340px;
}
</style>
